import apiSkobo from "@/helpers/api/apiSkobo";
import apiGraph from "@/helpers/api/apiGraph";
import graphHelper from "@/helpers/utilities/GraphHelper";

import { Assignment, AssignmentResource } from "@/models/Education/index";
import { CategoryExtension } from "@/models/extensions/education/CategoryExtension";

/**
 *  Actions for the assignments
 *
 * @module Assignments
 * @category Store
 * @subcategory Assignments
 * @name Actions
 */

export default {
  /**
   *  Load all assignments for a section if not in store
   *
   * @param { object } vuex Vuex
   * @param { vuex } vuex.state State
   * @param { vuex } vuex.dispatch Dispatch
   * @param { object } payload Payload
   * @param { string } payload.extSysApiSectionId External system section id
   * @param { string } payload.sectionId Id of section to load assignments for
   * @returns { Promise } Promise of loading assignments
   */
  async loadForSection({ state, dispatch }, { extSysApiSectionId, sectionId }) {
    if (!state.assignmentsForSectionMap[sectionId]) {
      dispatch("reloadForSection", { extSysApiSectionId, sectionId });
    }
  },

  /**
   *  Reload all assignments for a section
   *
   * @param { object } vuex Vuex
   * @param { vuex } vuex.commit Commit
   * @param { vuex } vuex.dispatch Dispatch
   * @param { object } payload Payload
   * @param { string } payload.extSysApiSectionId External system section id
   * @param { string } payload.sectionId Id of section to load assignments for
   * @returns { Promise } Promise of loading assignments
   */
  async reloadForSection({ commit, dispatch }, { extSysApiSectionId, sectionId }) {
    try {
      const response = await apiGraph.get(`/education/classes/${extSysApiSectionId}/assignments`);
      const assignments = response.data.value.map(a => Assignment.parseFromGraph(a, sectionId, extSysApiSectionId));
      const promises = [];

      promises.push(dispatch("loadSubmissions", assignments));

      promises.push(
        ...assignments.map(async assignment => {
          const response = await dispatch("loadFromSkobo", assignment.id);
          if (response) {
            assignment.gradeType = response.gradeType;
          }
        })
      );

      await Promise.all(promises);
      //await dispatch("loadCategoriesForAssignments", assignments);

      commit("loadForSection", {
        sectionId: sectionId,
        assignments: assignments
      });
    } catch {
      commit("loadForSection", {
        sectionId: sectionId,
        assignments: []
      });
    }
  },

  /**
   * Load assignment if it is not already loaded (or forced reload)
   *
   * @param { object } vuex Vuex
   * @param { vuex } vuex.state State
   * @param { vuex } vuex.dispatch Dispatch
   * @param { object } payload Payload
   * @param { object } payload.extSysApiSectionId External system section id
   * @param { object } payload.assignmentId Id of assignment to load
   * @param { object } payload.forceReload Force reloading the assignment instead of taking it from cache
   * @returns { Promise<object> } Assignment
   */
  async load({ state, dispatch }, { extSysApiSectionId, assignmentId, forceReload }) {
    if (state.assignments[assignmentId] && !forceReload) {
      return state.assignments[assignmentId];
    }

    return await dispatch("reloadForSection", { extSysApiSectionId, assignmentId });
  },

  /**
   * Reload assignment
   *
   * @param { object } vuex Vuex
   * @param { vuex } vuex.commit Commit
   * @param { object } payload Payload
   * @param { object } payload.extSysApiSectionId External system section id
   * @param { object } payload.assignmentId Id of assignment to load
   * @returns { Promise<object> } Assignment
   */
  async reload({ commit }, { extSysApiSectionId, assignmentId }) {
    const response = await apiGraph.get(`/education/classes/${extSysApiSectionId}/assignments/${assignmentId}`);

    commit("reload", response.data);
    return response.data;
  },

  loadCategoriesForAssignments({ commit }, assingmentList) {
    return new Promise((resolve, reject) => {
      const arr = [];
      for (let i = 0; i < assingmentList.length; i++) {
        arr.push(CategoryExtension.generateGraphRequestFromAssignment(assingmentList[i]));
      }

      graphHelper.makeBatchRequest(arr).then(
        result => {
          commit("loadCategoriesForAssignments", result);
          resolve();
        },
        error => reject(error)
      );
    });
  },

  async loadResources({ commit }, { extSysApiSectionId, assignmentId }) {
    const result = await apiGraph.get(`/education/classes/${extSysApiSectionId}/assignments/${assignmentId}/resources`);
    const resources = result.data.value.map(r => AssignmentResource.parseFromGraph(r));

    if (result) {
      commit("loadResources", { assignmentId, resources: resources });
    }
  },

  async save({ dispatch }, assignment) {
    if (assignment.id) {
      const patchedAssignment = await dispatch("updateAssignmentForSection", assignment);
      await dispatch("updateAssignmentMetas", assignment);
      return patchedAssignment;
    } else {
      const savedAssignment = await dispatch("createAssignmentForSection", assignment);
      assignment.id = savedAssignment.id;
      await dispatch("updateAssignmentMetas", assignment);
      return savedAssignment;
    }
  },

  /**
   * Create assignment in backend
   *
   * @param { object } vuex Vuex
   * @param { vuex } vuex.commit Commit
   * @param { vuex } vuex.dispatch Dispatch
   * @param { vuex } vuex.getters Getters
   * @param { object } assignment Assignment to create
   * @returns { Promise<object> } Created assignment
   */
  async createAssignmentForSection({ dispatch, commit, getters }, assignment) {
    const payload = assignment.createPayload;

    if (assignment.gradeType) {
      const gradeType = getters.GRADE_TYPES.find(gt => gt.id === assignment.gradeType);
      const largestValue = Math.max(...gradeType.grades.map(grade => grade.value));

      payload.grading.maxPoints = largestValue;
    } else {
      payload.grading.maxPoints = 0;
    }

    const response = await apiGraph.post(`/education/classes/${assignment.extSysApiSectionId}/assignments`, payload);

    assignment.id = response.data.id;
    await dispatch("saveToSkobo", assignment);

    const nctAssignment = Assignment.parseFromGraph(response.data, assignment.sectionId, assignment.extSysApiSectionId);

    commit("persistAssignmentForSection", {
      sectionId: assignment.sectionId,
      assignment: nctAssignment
    });

    return nctAssignment;
  },

  /**
   *Update assignment in backend
   *
   * @param { object } vuex Vuex
   * @param { vuex } vuex.dispatch Dispatch
   * @param { vuex } vuex.getters Getters
   * @param { vuex } vuex.commit Commit
   * @param { object } assignment Assignment to create
   * @returns { Promise<object> } Updated assignment
   */

  async updateAssignmentForSection({ dispatch, commit, getters }, assignment) {
    const payload = assignment.patchPayload;

    if (assignment.gradeType) {
      const gradeType = getters.GRADE_TYPES.find(gt => gt.id === assignment.gradeType);
      const largestValue = Math.max(...gradeType.grades.map(grade => grade.value));

      payload.grading.maxPoints = largestValue;
    } else {
      payload.grading.maxPoints = 0;
    }

    const response = await apiGraph.patch(
      `/education/classes/${assignment.extSysApiSectionId}/assignments/${assignment.id}`,
      payload
    );

    await dispatch("saveToSkobo", assignment);

    const nctAssignment = Assignment.parseFromGraph(response.data, assignment.sectionId, assignment.extSysApiSectionId);

    commit("persistAssignmentForSection", {
      sectionId: assignment.sectionId,
      assignment: nctAssignment
    });
    return nctAssignment;
  },

  updateAssignmentMetas({ dispatch }, assignment) {
    const promises = [];
    promises.push(dispatch("updateAssignmentResources", assignment));
    return Promise.all(promises);
  },

  // eslint-disable-next-line no-empty-pattern
  async updateAssignmentResources({}, assignment) {
    if (assignment.resources) {
      const promises = assignment.resources.map(async res => {
        if (res.isDirty) {
          if (res.delete) {
            await apiGraph.delete(
              `/education/classes/${assignment.extSysApiSectionId}/assignments/${assignment.id}/resources/${res.id}`
            );
            res = null;
          } else if (!res.id) {
            const createResourceResponse = await apiGraph.post(
              `/education/classes/${assignment.extSysApiSectionId}/assignments/${assignment.id}/resources`,
              res.requestPayload
            );

            return AssignmentResource.parseFromGraph(createResourceResponse.data);
          } else {
            await apiGraph.delete(
              `/education/classes/${assignment.extSysApiSectionId}/assignments/${assignment.id}/resources/${res.id}`
            );
            const updateResourceResponse = await apiGraph.post(
              `/education/classes/${assignment.extSysApiSectionId}/assignments/${assignment.id}/resources`,
              res.requestPayload
            );

            return AssignmentResource.parseFromGraph(updateResourceResponse.data);
          }
        } else {
          return res;
        }
      });

      const resultingResources = await Promise.all(promises);
      assignment.resources = resultingResources.filter(b => !!b);
    }
  },

  deleteAssignment({ commit, state }, assignmentId) {
    return new Promise((resolve, reject) => {
      const assignment = state.assignments[assignmentId];

      apiGraph.delete(`/education/classes/${assignment.extSysApiSectionId}/assignments/${assignmentId}`).then(
        response => {
          commit("deleteAssignment", assignmentId);
          resolve(response);
        },
        error => reject(error)
      );
    });
  },

  // eslint-disable-next-line no-empty-pattern
  async publish({}, { extSysApiSectionId, assignmentId }) {
    await apiGraph.post(`/education/classes/${extSysApiSectionId}/assignments/${assignmentId}/publish`);
  },

  /**
   * Create resources folder on assignment
   *
   * @param { object } vuex Vuex
   * @param { vuex } vuex.getters Getters
   * @param { object } payload Payload
   * @param {  payload } payload.extSysApiSectionId Id of the section the assignment is related to
   * @param {  payload } payload.assignmentId Id of the assignment
   * @returns {Promise<string>} Promise with weburl for assignment resourcesfolder
   */
  async createResourcesFolder({ getters }, { extSysApiSectionId, assignmentId }) {
    const noOfRetry = 5;
    let levelOfRetry = 0;

    if (!assignmentId || !extSysApiSectionId) {
      throw new Error("Input not valid");
    }

    if (
      getters.ASSIGNMENT(assignmentId).resourcesFolderUrl &&
      getters.ASSIGNMENT(assignmentId).resourcesFolderUrl !== ""
    ) {
      return getters.ASSIGNMENT(assignmentId).resourcesFolderUrl;
    }

    return await new Promise((resolve, reject) => {
      const intervaller = setInterval(async () => {
        try {
          const result = await apiGraph.post(
            `/education/classes/${extSysApiSectionId}/assignments/${assignmentId}/setUpResourcesFolder`
          );
          if (result && result.status === 200 && result.data && result.data.resourcesFolderUrl) {
            resolve(result.data.resourcesFolderUrl);
            clearInterval(intervaller);
          }
          if (
            result &&
            result.status === 400 &&
            result.innerError &&
            result.innerError.code === "folderAlreadyExists"
          ) {
            resolve();
          }
        } finally {
          if (levelOfRetry > noOfRetry) {
            reject();
            clearInterval(intervaller);
          }
          levelOfRetry++;
        }
      }, 2000);
    });
  },

  /***********************************
   * Grades
   ***********************************/

  async loadGradeTypes({ commit, state }) {
    if (state.gradeTypes && state.gradeTypes.length > 0) {
      return;
    }

    const response = await apiSkobo.get("/assignment/setup");
    commit("loadGradeTypes", response.data);
  },

  // eslint-disable-next-line no-empty-pattern
  async saveToSkobo({}, assignment) {
    const assignmentObj = {
      gradeType: assignment.gradeType,
      title: assignment.displayName
    };

    await apiSkobo.post(`/assignment/${assignment.sectionId}/${assignment.id}`, assignmentObj);
  },

  // eslint-disable-next-line no-empty-pattern
  async loadFromSkobo({}, assignmentId) {
    const response = await apiSkobo.get(`/assignment/${assignmentId}`);

    return response.data ? response.data.assignment : null;
  }
};
